import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom, switchMap } from 'rxjs/operators';
import { ChecklistsService, ChecklistsSelectors } from './services';
import * as ChecklistsActions from './checklists.actions';
import { ScookieService, AuthSelectors, ClientsService } from '@store';
import { Checklist, PatchObject, ChecklistMoms2, ChecklistLon, Adhoc } from '@store/models';

@Injectable()
export class ChecklistsEffects {

    loadHeaders$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadHeaders),
        mergeMap(() => this.checklistsService.getHeaders().pipe(
            map(headers => ChecklistsActions.loadHeadersSuccess({ headers }),
            catchError(() => EMPTY))
        ))
    ));

    loadAdhocHeaders$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadAdhocHeaders),
        mergeMap(() => this.checklistsService.getAdhocHeaders().pipe(
            map(adhocHeaders => ChecklistsActions.loadAdhocHeadersSuccess({ adhocHeaders }),
            catchError(() => EMPTY))
        ))
    ));

    loadPeriod$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadPeriod),
        mergeMap((action) => this.scookieService.getCookie('checklistPeriod', 1).pipe(
            map(period => ChecklistsActions.loadPeriodSuccess({ period, page: action.page }),
            catchError(() => EMPTY))
            )
        ))
    );

    loadPeriodSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadPeriodSuccess),
        map(action => this.pageLoadAction(action.page, action.period)),
        catchError(() => EMPTY))
    );

    setPeriod$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.setPeriod),
        mergeMap(action => {
            const period = action.period;
            const page = action.page;
            return this.scookieService.saveCookieUnique('checklistPeriod', period.toString()).pipe(
                map(() => this.pageLoadAction(page, period)),
                catchError(() => EMPTY)
            );
        })
    ));

    loadMonth$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadMonth),
        mergeMap(() => this.scookieService.getCookie('checklistMonth', 1).pipe(
            map(month => ChecklistsActions.loadMonthSuccess({ month }),
            catchError(() => EMPTY))
            )
        ))
    );

    setMonth$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.setMonth),
        map(action => {
            const month = action.month;
            this.scookieService.saveCookieUnique('checklistMonth', month.toString()).subscribe();
            this.scookieService.saveCookieUnique('checklistWeek', 'undefined').subscribe();

        })
    ), { dispatch: false });

    loadWeek$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadWeek),
        mergeMap(() => this.scookieService.getCookie('checklistWeek', 1).pipe(
            map(week => ChecklistsActions.loadWeekSuccess({ week }),
            catchError(() => EMPTY))
            )
        ))
    );

    setWeek$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.setWeek),
        map(action => {
            const week = action.week;
            this.scookieService.saveCookieUnique('checklistWeek', week.toString()).subscribe();
            this.scookieService.saveCookieUnique('checklistMonth', 'undefined').subscribe();
        })
    ), { dispatch: false });

    loadChecklist$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadChecklist),
        withLatestFrom(this.checklistsSelector.period$),
        mergeMap(([action, period]) => {
            const getPeriod = action.period ? action.period : period;
            return this.checklistsService.getChecklister(getPeriod).pipe(
                map(
                    checklists => ChecklistsActions.loadChecklistSuccess({ checklists }),
                    catchError(() => EMPTY)
                )
            );
        })
    ));

    setMyClientsFilter$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.setMyClientsFilter),
        withLatestFrom(
            this.authSelector.userId$,
            this.checklistsSelector.filterOptions$
        ),
        map(([, userid, filterOptions]) => {
            const partialFilterOptions = { myClients: filterOptions.myClients === -1 ? userid : -1};
            return ChecklistsActions.setFilter({ partialFilterOptions });
        })
    ));

    selskabToggle$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.selskabToggle),
        withLatestFrom(
            this.checklistsSelector.filterOptions$
        ),
        map(([, filterOptions]) => {
            const partialFilterOptions = { selskab: !filterOptions.selskab};
            return ChecklistsActions.setFilter({ partialFilterOptions });
        })
    ));

    privatToggle$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.privatToggle),
        withLatestFrom(
            this.checklistsSelector.filterOptions$
        ),
        map(([, filterOptions]) => {
            const partialFilterOptions = { privat: !filterOptions.privat};
            return ChecklistsActions.setFilter({ partialFilterOptions });
        })
    ));

    loadFilter$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadFilter),
        mergeMap(() => this.scookieService.getJsonCookie('checklistFilter').pipe(
            map(filterOptions => ChecklistsActions.loadFilterSuccess({ filterOptions }),
            catchError(() => EMPTY))
            )
        ))
    );

    setFilter$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.setFilter),
        withLatestFrom(this.checklistsSelector.filterOptions$),
        mergeMap(([action, filterOptions]) => {
            const newFilterOptions = {
                ...filterOptions,
                ...action.partialFilterOptions,
                selskab: false,
                privat: false
            };
            return this.scookieService.saveJsonCookieUnique('checklistFilter', newFilterOptions).pipe(
                map(() => {
                    const saUser = newFilterOptions.advanced?.find(f => f.key === 'sa')?.value as string;
                    if (!saUser) { return ChecklistsActions.loadFilterKids({username: ''}); }
                    return ChecklistsActions.loadFilterKids({username: saUser});
                }),
                catchError(() => of(ChecklistsActions.loadFilterKids({username: ''})))
            );;
        })
    ));

    loadFilterKids$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadFilterKids),
        switchMap(action => {
            if (!action.username) { return of(ChecklistsActions.loadFilterKidsSuccess({ filterKids: undefined })); }
            return this.clientsService.getCaseKidsByUsername(action.username).pipe(
                map(filterKids => ChecklistsActions.loadFilterKidsSuccess({ filterKids })),
                catchError(() => of(ChecklistsActions.loadFilterKidsSuccess({ filterKids: undefined })))
            );
        })
    ));

    createChecklist$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.createChecklist),
        mergeMap(action => {
            let newChecklist = new Checklist();
            newChecklist = {...newChecklist, ...action.partialChecklist};
            return this.checklistsService.createChecklist(newChecklist).pipe(
                map(checklist => ChecklistsActions.createChecklistSuccess({ checklist })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateChecklist$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.updateChecklist),
        withLatestFrom(this.checklistsSelector.checklists$),
        mergeMap(([action, checklists]) => {
            const newChecklist = action.checklist;
            const oldChecklist = checklists.find(c => c.id === newChecklist.id);
            const patchValues: PatchObject[] = [];
            Object.keys(newChecklist).forEach(key => {
                if (newChecklist[key] !== oldChecklist[key] && !(!newChecklist[key] && !oldChecklist[key])) {
                    patchValues.push({value: newChecklist[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.checklistsService.updateChecklist(patchValues, newChecklist.id).pipe(
                map(checklist => ChecklistsActions.updateChecklistSuccess({ checklist })),
                catchError(() => EMPTY)
            );
        })
    ));

// Moms2 Effects
    loadChecklistMoms2$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadChecklistMoms2),
        withLatestFrom(this.checklistsSelector.period$),
        mergeMap(([action, period]) => {
            const getPeriod = action.period ? action.period : period;
            return this.checklistsService.getChecklisterMoms2(getPeriod).pipe(
                map(
                    checklistsMoms2 => ChecklistsActions.loadChecklistMoms2Success({ checklistsMoms2 }),
                    catchError(() => EMPTY)
                )
            );
        })
    ));

    createChecklistMoms2$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.createChecklistMoms2),
        mergeMap(action => {
            let newChecklist = new ChecklistMoms2();
            newChecklist = {...newChecklist, ...action.partialChecklistMoms2};
            return this.checklistsService.createChecklistMoms2(newChecklist).pipe(
                map(checklistMoms2 => ChecklistsActions.createChecklistMoms2Success({ checklistMoms2 })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateChecklistMoms2$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.updateChecklistMoms2),
        withLatestFrom(this.checklistsSelector.checklistsMoms2$),
        mergeMap(([action, checklists]) => {
            const newChecklist = action.checklistMoms2;
            const oldChecklist = checklists.find(c => c.id === newChecklist.id);
            const patchValues: PatchObject[] = [];
            Object.keys(newChecklist).forEach(key => {
                if (newChecklist[key] !== oldChecklist[key] && !(!newChecklist[key] && !oldChecklist[key])) {
                    patchValues.push({value: newChecklist[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.checklistsService.updateChecklistMoms2(patchValues, newChecklist.id).pipe(
                map(checklistMoms2 => ChecklistsActions.updateChecklistMoms2Success({ checklistMoms2 })),
                catchError(() => EMPTY)
            );
        })
    ));

// Lon Effects
    loadChecklistLon$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadChecklistLon),
        withLatestFrom(this.checklistsSelector.period$),
        mergeMap(([action, period]) => {
            const getPeriod = action.period ? action.period : period;
            return this.checklistsService.getChecklisterLon(getPeriod).pipe(
                map(
                    checklistsLon => ChecklistsActions.loadChecklistLonSuccess({ checklistsLon }),
                    catchError(() => EMPTY)
                )
            );
        })
    ));

    createChecklistLon$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.createChecklistLon),
        mergeMap(action => {
            let newChecklist = new ChecklistLon();
            newChecklist = {...newChecklist, ...action.partialChecklistLon};
            return this.checklistsService.createChecklistLon(newChecklist).pipe(
                map(checklistLon => ChecklistsActions.createChecklistLonSuccess({ checklistLon })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateChecklistLon$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.updateChecklistLon),
        withLatestFrom(this.checklistsSelector.checklistsLon$),
        mergeMap(([action, checklists]) => {
            const newChecklist = action.checklistLon;
            const oldChecklist = checklists.find(c => c.id === newChecklist.id);
            const patchValues: PatchObject[] = [];
            Object.keys(newChecklist).forEach(key => {
                if (newChecklist[key] !== oldChecklist[key] && !(!newChecklist[key] && !oldChecklist[key])) {
                    patchValues.push({value: newChecklist[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.checklistsService.updateChecklistLon(patchValues, newChecklist.id).pipe(
                map(checklistLon => ChecklistsActions.updateChecklistLonSuccess({ checklistLon })),
                catchError(() => EMPTY)
            );
        })
    ));

    // Adhhoc Effects
    loadAdhoc$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.loadAdhoc),
        withLatestFrom(this.checklistsSelector.period$),
        mergeMap(([action, period]) => {
            const getPeriod = action.period ? action.period : period;
            return this.checklistsService.getAdhoc(getPeriod).pipe(
                map(
                    adhocs => ChecklistsActions.loadAdhocSuccess({ adhocs }),
                    catchError(() => EMPTY)
                )
            );
        })
    ));

    createAdhoc$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.createAdhoc),
        mergeMap(action => {
            let newAdhoc = new Adhoc();
            newAdhoc = {...newAdhoc, ...action.partialAdhoc};
            return this.checklistsService.createAdhoc(newAdhoc).pipe(
                map(adhoc => ChecklistsActions.createAdhocSuccess({ adhoc })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateAdhoc$ = createEffect(() => this.actions$.pipe(
        ofType(ChecklistsActions.updateAdhoc),
        withLatestFrom(this.checklistsSelector.adhocs$),
        mergeMap(([action, adhocs]) => {
            const newAdhoc = action.adhoc;
            const oldAdhoc = adhocs.find(c => c.id === newAdhoc.id);
            const patchValues: PatchObject[] = [];
            Object.keys(newAdhoc).forEach(key => {
                if (newAdhoc[key] !== oldAdhoc[key] && !(!newAdhoc[key] && !oldAdhoc[key])) {
                    patchValues.push({value: newAdhoc[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.checklistsService.updateAdhoc(patchValues, newAdhoc.id).pipe(
                map(adhoc => ChecklistsActions.updateAdhocSuccess({ adhoc })),
                catchError(() => EMPTY)
            );
        })
    ));

    constructor(
        private actions$: Actions,
        private checklistsService: ChecklistsService,
        private scookieService: ScookieService,
        private checklistsSelector: ChecklistsSelectors,
        private authSelector: AuthSelectors,
        private clientsService: ClientsService
    ) {}

    pageLoadAction(page: string, period: number) {
        if (page && page === 'moms2') {
            return ChecklistsActions.loadChecklistMoms2({ period });
        }
        if (page && page === 'lon') {
            return ChecklistsActions.loadChecklistLon({ period });
        }
        if (page && page === 'adhoc') {
            return ChecklistsActions.loadAdhoc({ period });
        }
        return ChecklistsActions.loadChecklist({ period });
    }

}
